<template>
    <div class="card">

        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="false"
                :showAddNew="false"
                :title="title"
            >
            </TitleButton>
        </div>

        <div class="row p-2">
            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <div class="d-flex justify-content-between">
                    <label class="form-label">Select Customer <span class="text-danger">*</span></label>
                    <button @click="openCustomerModal" class="btn btn-primary btn-sm">
                        +
                    </button>
                </div>
                
                <AsyncSelect
                    placeholder="Select Customer"
                    v-model="contactProfile"
                    :api-service="fetchContactProfiles"
                    :format-label="formatPatientLabel"
                />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label class="form-label">Select Deal Source <span class="text-danger">*</span></label>
                <v-select
                    placeholder="Select Deal Source"
                    :options="dealSourceList" v-model="formData.deal_source_id"
                    :reduce="option => option.id"
                    label="name"
                />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label class="form-label">Select Deal Owner</label>
                <AsyncSelect
                    placeholder="Deal Owner"
                    v-model="dealOwner"
                    :api-service="fetchContactProfiles"
                    :format-label="formatPatientLabel"
                    :additional-query="{ type: 'contact_owner' }"
                />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label class="form-label">Select Deal Assign to</label>
                <AsyncSelect
                    placeholder="Deal Assign to"
                    v-model="dealAssign"
                    :api-service="fetchContactProfiles"
                    :format-label="formatPatientLabel"
                />
            </div>
        </div>

        <div class="row px-2">
            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label class="form-label">Select Deal Stage</label>
                <v-select
                    placeholder="Select Deal Stage"
                    :options="dealStageList" v-model="formData.deal_stage_id"
                    :reduce="option => option.id"
                    label="name"
                />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label class="form-label">Select Project</label>
                <AsyncSelect
                    placeholder="Project"
                    v-model="project"
                    :api-service="fetchProjects"
                    :format-label="formatPatientLabel"
                />
            </div>
        </div>

        <div class="p-2">
            <ul class="nav nav-tabs">
                <li v-for="tab in tabs" :key="tab.id" class="nav-item">
                    <a
                        :class="['nav-link', { active: activeTab.id == tab.id }]"
                        @click="setActiveTab(tab)"
                    >
                      {{ tab.title }}
                    </a>
                </li>
            </ul>
        </div>

        <div class="row justify-content-center">
            <component
                :is="tabComponents[activeTab.id]"
                :priorityList="priorityList"
                ref="childRef"
            />
        </div>

        <div class="row py-2 justify-content-center">
            <div class="col-3">
                <button class="btn btn-secondary" @click="onClickCancel">
                    Cancel
                </button>
            </div>
            <div v-if="route.params.salePipelineId" class="col-3">
                <button class="btn btn-primary" @click="updateData">
                    update
                </button>
            </div>
            <div v-else class="col-3">
                <button class="btn btn-primary" @click="storeData">
                    Save
                </button>
            </div>
        </div>
        <AddCustomerModal 
            v-if="store.state.isModalOpenTwo"
        />
    </div>
</template>

<script setup>
import TitleButton from '@/components/molecule/company/crm/visit-plan/VisitTitle.vue';
import DealInformation from '@/components/molecule/company/admin/open-deal/DealInformation.vue';
import Quotation from '@/components/molecule/company/admin/open-deal/Quotation.vue';
import Task from '@/components/molecule/company/admin/open-deal/Task.vue';
import { computed, inject, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import handleContact from "@/services/modules/contact";
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import { useAsyncDropdownHelper } from "@/services/utils/asyncDropdownHelper";
import handleHospital from '@/services/modules/hospital';
import handleProjects from "@/services/modules/procurement/project";
import handleAdmin from '@/services/modules/admin/admin';
import AddCustomerModal from '@/components/molecule/company/inventory/AddCustomerModal.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const dealSourceList = ref([]);
const dealStageList = ref([]);
const priorityList = ref([]);
const childRef = ref(null);
const title = computed(() => route.params.salePipelineId ? 'Update Deal' : 'Open a Deal');
const contactProfile = ref(null);
const dealOwner = ref(null);
const dealAssign = ref(null);
const project = ref(null);

const formData = ref({
    company_id: route.params.companyId,
    contact_profile_id: null,
    deal_assign_id: null,
    deal_owner_id: null,
    deal_source_id: null,
    deal_stage_id: null,
    project_id: null
});

const tabs = [
    { id: 'deal', title: 'Deal Information' },
    { id: 'quotation', title: 'Quotation' },
    { id: 'task', title: 'Task' }
];

const tabComponents = {
    'deal': DealInformation,
    'quotation': Quotation,
    'task': Task
};

const activeTab = ref(tabs[0]);

const { fetchContactProfiles } = handleContact();
const { formatPatientLabel } = useAsyncDropdownHelper();
const { fetchProjects } = handleProjects()
const { fetchNoteMasterList } = handleHospital();
const { storeOpenDeal, fetchPipelineById, updatePipeline } = handleAdmin();

const setActiveTab = (tab) => {
    activeTab.value = tab;
};

const getDealList = async (noteType, targetList) => {
    const query = `?company_id=${route.params.companyId}&note_type=${noteType}`;
     try {
        const res = await fetchNoteMasterList(query);
        if (!res.status) {
            return showError(res.message);
        }
        targetList.value = res.data.data;
    } catch (err) {
        console.error(err);
    }
};

const checkValidation = (data) => {
    if(! data.contact_profile_id ) {
        showError('Select customer');
        return false;
    }

    if(! data.deal_source_id ) {
        showError('Select source');
        return false;
    }

    if(! data.deal_title ) {
        showError('Select title');
        return false;
    }

    return true;
}

const storeData = async () => {
    const data = mergedFormData();
    const validation = checkValidation(data);

    if(! validation) {
        return;
    }

    try {
        const res = await storeOpenDeal(data);
        if (!res.status) {
            return showError(res.message);
        }
        showSuccess(res.message);
        routePush();
    } catch (err) {
        console.error(err);
    }
}

const updateData = async () => {
    const data = mergedFormData();
    const validation = checkValidation(data);
    const id = route.params.salePipelineId;

    if(! validation) {
        return;
    }

    try {
        const res = await updatePipeline(id, data);
        if (!res.status) {
            return showError(res.message);
        }
        showSuccess(res.message);
        routePush();
    } catch (err) {
        console.error(err);
    }

}

const getPipelineById = async (id) => {
    const query = `?company_id=${route.params.companyId}`;

    try {
        const res = await fetchPipelineById(id, query);
        if (!res.status) {
            return showError(res.message);
        }
        processPipelineData(res.data);
    } catch (err) {
        console.error(err);
    }
}

const mergedFormData = () => {
    const data = childRef.value.getFormData();
    const mergedFormData = {
        ...formData.value,
        ...data
    };

    return mergedFormData;
}

const processPipelineData = (data) => {
    formData.value = {
        company_id: route.params.companyId,
        contact_profile_id: data.contact_profile_id,
        deal_assign_id: data.deal_assign_id,
        deal_source_id: data.deal_source_id,
        deal_owner_id: data.deal_owner_id,
        deal_stage_id: data.deal_stage_id,
        project_id: data.project_master_id
    };

    contactProfile.value = {
        id: data.contact_profile_id,
        name: data.contact_profile_name,
    }

    dealOwner.value = {
        id: data.deal_owner_id,
        name: data.deal_owner_name,
    }

    dealAssign.value = {
        id: data.deal_assign_id,
        name: data.deal_assign_name,
    }

    project.value = {
        id: data.project_master_id,
        name: data.project_name,
    }

    childRef.value.setFormData({
        deal_title: data.deal_title,
        description: data.description,
        est_deal_value: data.est_deal_value,
        exp_closing_date: data.exp_closing_date,
        probability: data.probability,
        priority_id: data.priority_id,
        item_details: data.products,
        tag_list: data.tag_registers
    });
}

const onClickCancel = () => {
    router.back();
}

const routePush = () => {
    router.push({
      name: 'sale-pipeline-list',
      params: route.params,
      query: route.query,
    })
}

const openCustomerModal = () => {
    store.state.isModalOpenTwo = true;
}

watch(contactProfile, (newValue) => {
    formData.value.contact_profile_id = newValue ? newValue.id : null;
});

watch(dealOwner, (newValue) => {
    formData.value.deal_owner_id = newValue ? newValue.id : null;
});

watch(dealAssign, (newValue) => {
    formData.value.deal_assign_id = newValue ? newValue.id : null;
});

watch(project, (newValue) => {
    formData.value.project_id = newValue ? newValue.id : null;
});

onMounted( () => {
    getDealList('deal_source', dealSourceList);
    getDealList('deal_stage', dealStageList);
    getDealList('priority', priorityList);
    if(route.params.salePipelineId) {
        getPipelineById(route.params.salePipelineId);
    }
})
</script>

<style scoped>
.mr-25 {
  margin-right: 25px;
}
.mr-top-1 {
    margin-top: 1% !important;
}
.filter-label {
    padding-right: 0.5rem !important;
}
</style>